import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, X, Check, Edit3, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

export interface DocumentationChange {
  before: string;
  after: string;
  reason: string;
}

export interface DocumentationSuggestion {
  original: string;
  suggested: string;
  changes: DocumentationChange[];
  confidence: number;
  unchanged: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  suggestion: DocumentationSuggestion | null;
  onAccept: (finalText: string) => Promise<void>;
}

/**
 * Documentation Assistant modal — shows the original treatment note alongside
 * an LLM-suggested rewrite that is friendlier to the CPT code extractor.
 *
 * Principles:
 *  - Never replaces text automatically. Physician accepts, edits, or dismisses.
 *  - Shows specific changes so the physician sees EXACTLY what was touched.
 *  - Never invents clinical details — the LLM is instructed not to fabricate,
 *    and changes are shown verbatim so the physician can catch any drift.
 */
export function DocumentationAssistantModal({
  open, onClose, loading, suggestion, onAccept,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const startEditing = () => {
    setEditedText(suggestion?.suggested ?? '');
    setEditing(true);
  };

  const handleAccept = async (text: string) => {
    setSubmitting(true);
    try {
      await onAccept(text);
      onClose();
      setEditing(false);
      setEditedText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={submitting ? undefined : onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-violet-50 to-indigo-50">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-lg text-gray-900">Documentation Assistant</h3>
              </div>
              <button
                onClick={onClose}
                disabled={submitting}
                className="p-1 rounded hover:bg-white/60 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading && (
                <div className="flex items-center justify-center py-16 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Analyzing your note…
                </div>
              )}

              {!loading && suggestion?.unchanged && (
                <div className="p-8 text-center text-gray-700">
                  <p className="text-sm">
                    Your note is already clear and extractor-friendly. No changes suggested.
                  </p>
                </div>
              )}

              {!loading && suggestion && !suggestion.unchanged && (
                <div className="p-4 space-y-4">
                  {/* Before / After grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">Original</div>
                      <div className="border rounded-lg p-3 bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap font-sans">
                        {suggestion.original}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-violet-700 mb-1 flex items-center gap-1">
                        Suggested
                        <ArrowRight className="w-3 h-3" />
                      </div>
                      {editing ? (
                        <textarea
                          value={editedText}
                          onChange={(e) => setEditedText(e.target.value)}
                          className="w-full border rounded-lg p-3 text-sm h-40 font-sans"
                          placeholder="Edit the suggested text…"
                        />
                      ) : (
                        <div className="border rounded-lg p-3 bg-violet-50 text-sm text-gray-800 whitespace-pre-wrap font-sans">
                          {suggestion.suggested}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Changes list */}
                  {suggestion.changes.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">
                        Why ({suggestion.changes.length} change{suggestion.changes.length === 1 ? '' : 's'})
                      </div>
                      <ul className="space-y-1.5">
                        {suggestion.changes.map((c, i) => (
                          <li key={i} className="border rounded p-2 text-xs text-gray-700 bg-white">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="line-through text-red-600">{c.before}</span>
                              <ArrowRight className="w-3 h-3 text-gray-400" />
                              <span className="text-emerald-700 font-medium">{c.after}</span>
                            </div>
                            <div className="text-gray-500 mt-0.5 italic">{c.reason}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidence footer */}
                  {typeof suggestion.confidence === 'number' && (
                    <div className="text-xs text-gray-500 border-t pt-2">
                      Preservation confidence: {Math.round(suggestion.confidence * 100)}%. The assistant is instructed
                      NOT to invent clinical details — compare the suggestion with the original to confirm accuracy.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            {!loading && suggestion && !suggestion.unchanged && (
              <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
                <Button variant="ghost" onClick={onClose} disabled={submitting}>
                  Dismiss
                </Button>
                {!editing && (
                  <Button variant="outline" onClick={startEditing} disabled={submitting}>
                    <Edit3 className="w-4 h-4 mr-1.5" /> Edit first
                  </Button>
                )}
                <Button
                  onClick={() => handleAccept(editing ? editedText : suggestion.suggested)}
                  disabled={submitting || (editing && !editedText.trim())}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Applying…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1.5" /> Accept &amp; use
                    </>
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
